/**
 * Color Helper Utilities
 *
 * Map capacity metrics to colors for visual indicators
 */

/**
 * Get color based on surplus percentage
 *
 * Green: >30% surplus (<70% utilization)
 * Orange: 20-30% surplus (70-80% utilization)
 * Red: <20% surplus (>80% utilization)
 */
export function getSurplusColor(surplusPercent: number): string {
  if (surplusPercent > 30) {
    return '#4caf50'; // Green - good capacity
  } else if (surplusPercent >= 20) {
    return '#ff9800'; // Orange - tight capacity
  } else {
    return '#f44336'; // Red - over capacity/not recommended
  }
}

/**
 * Get color based on gap minutes
 *
 * Positive gap = surplus capacity
 * Negative gap = over capacity
 */
export function getGapColor(gapMinutes: number): string {
  const surplusPercent = gapMinutes > 0 ? 100 : -100; // Simplified for visual
  return getSurplusColor(surplusPercent);
}

/**
 * Get MUI color name based on surplus
 */
export function getSurplusColorName(surplusPercent: number): 'success' | 'warning' | 'error' {
  if (surplusPercent > 30) {
    return 'success';
  } else if (surplusPercent >= 20) {
    return 'warning';
  } else {
    return 'error';
  }
}

/**
 * Get background color with opacity for cells
 */
export function getCellBackgroundColor(surplusPercent: number): string {
  if (surplusPercent > 30) {
    return 'rgba(76, 175, 80, 0.1)'; // Light green - <70% util
  } else if (surplusPercent >= 20) {
    return 'rgba(255, 152, 0, 0.1)'; // Light orange - 70-80% util
  } else {
    return 'rgba(244, 67, 54, 0.15)'; // Light red - >80% util
  }
}

/**
 * Get color based on visit capacity count
 *
 * Green: 2+ visits (good capacity)
 * Orange: 1 visit (tight capacity)
 * Red: <1 visit (no capacity)
 */
export function getVisitCapacityColor(visitCount: number): string {
  if (visitCount >= 2) {
    return '#4caf50'; // Green
  } else if (visitCount >= 1) {
    return '#ff9800'; // Orange
  } else {
    return '#f44336'; // Red
  }
}

/**
 * Get background color for visit capacity cells
 */
export function getVisitCapacityBackgroundColor(visitCount: number): string {
  if (visitCount >= 2) {
    return 'rgba(76, 175, 80, 0.1)'; // Light green
  } else if (visitCount >= 1) {
    return 'rgba(255, 152, 0, 0.1)'; // Light orange
  } else {
    return 'rgba(244, 67, 54, 0.15)'; // Light red
  }
}
